import type { Contact } from '@/entities/contacts';
import { BaseQueryParams } from '@/shared/api';
import type { ContactFormValues } from '../model/validation';

/**
 * Transforms API response to form values for the contact form
 * @param contact The API response data for a contact
 * @returns The form values populated with the API response data
 */
export const transformContactToForm = (contact: Partial<Contact>): ContactFormValues => {
  return {
    title: contact.title || '',
    name: contact.name || '',
    jobTitle: contact.jobTitle || '',
    address: contact.address || '',
    email1: contact.email1 || '',
    email2: contact.email2 || '',
    email3: contact.email3 || '',
    phone1: contact.phone1 || '',
    phone2: contact.phone2 || '',
    phone3: contact.phone3 || '',
    orgId: contact.orgId
  };
};

/**
 * Transforms form values to API request format for creating/updating a contact
 * @param formValues The form values from the contact form
 * @returns The API request object for creating/updating the contact
 */
export const transformFormToContact = (formValues: ContactFormValues): Partial<Contact> => {
  const result: Partial<Contact> = {
    // Always include required fields
    name: formValues.name,
    email1: formValues.email1,
    phone1: formValues.phone1
  };

  // Add optional fields only if they have values
  if (formValues.title) result.title = formValues.title;
  if (formValues.jobTitle) result.jobTitle = formValues.jobTitle;
  if (formValues.address) result.address = formValues.address;
  if (formValues.email2) result.email2 = formValues.email2;
  if (formValues.email3) result.email3 = formValues.email3;
  if (formValues.phone2) result.phone2 = formValues.phone2;
  if (formValues.phone3) result.phone3 = formValues.phone3;
  if (formValues.orgId !== undefined) result.orgId = formValues.orgId;

  return result;
};

/**
 * Builds the complete create request for a new contact
 * @param formValues The form values from the contact form
 * @param projectId The project ID
 * @param siteId The site ID
 * @returns The complete request object for creating a contact
 */
export const buildCreateRequest = (
  formValues: ContactFormValues,
  projectId: number,
  siteId: number
): Partial<Contact> & BaseQueryParams => {
  return {
    ...transformFormToContact(formValues),
    projectId,
    siteId
  };
};

/**
 * Builds the complete update request for an existing contact
 * @param formValues The form values from the contact form
 * @param contactId The contact ID to update
 * @param projectId The project ID
 * @param siteId The site ID
 * @returns The complete request object for updating a contact
 */
export const buildUpdateRequest = (
  formValues: ContactFormValues,
  contactId: string,
  projectId: number,
  siteId: number
): Partial<Contact> & BaseQueryParams => {
  return {
    ...transformFormToContact(formValues),
    id: contactId,
    projectId,
    siteId
  };
};
